import React, { useState } from 'react';
import { TestCard } from '@/components/TestCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { labTests, testCategories } from '@/data/tests';
import { Search, Filter } from 'lucide-react';

export default function Tests() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter tests based on category and search
  const filteredTests = labTests.filter((test) => {
    const matchesCategory =
      selectedCategory === 'all' ||
      test.category.toLowerCase().includes(selectedCategory);
    const matchesSearch =
      searchQuery === '' ||
      test.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryDisplayName = (categoryId) => {
    const category = testCategories.find((cat) => cat.id === categoryId);
    return category ? category.name : 'All Tests';
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header Section */}
      <section className="py-12 md:py-16 bg-gradient-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              All Diagnostic Tests
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Browse through our comprehensive list of diagnostic tests
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search for tests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base shadow-card"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-border bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Filter by Category:
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {testCategories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category.id)}
                className="transition-smooth"
              >
                {category.name}
                <Badge
                  variant="secondary"
                  className="ml-2 bg-background/20 text-xs"
                >
                  {category.count}
                </Badge>
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Tests Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Count */}
          <div className="mb-6">
            <p className="text-sm text-muted-foreground">
              Showing {filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''}
              {selectedCategory !== 'all' && (
                <span> in {getCategoryDisplayName(selectedCategory)}</span>
              )}
              {searchQuery && (
                <span> matching "{searchQuery}"</span>
              )}
            </p>
          </div>

          {/* Tests Grid */}
          {filteredTests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTests.map((test) => (
                <TestCard key={test.id} test={test} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex p-6 bg-muted/50 rounded-2xl mb-4">
                <Search className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No tests found
              </h3>
              <p className="text-muted-foreground mb-6">
                Try adjusting your search or filter criteria
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
